import React, { useEffect, useRef } from 'react';
import { Spin, message } from 'antd';
import { useTranslation } from 'react-i18next';
import { useFetchSingleShipment } from '@/hooks';
import { FEDEX_ACCOUNT_MISSING_MESSAGE } from './FedExPickupControls';
import { usePickupPrefill } from '@/hooks/useFedExPickup';
import type { ToggleDrawerFn } from '../ControlTower/types';
import SchedulePickupPanel from './SchedulePickupPanel';

interface SchedulePickupDrawerProps {
  shipmentId: string;
  legType?: 'CL' | 'EL';
  toggleDrawer: ToggleDrawerFn;
}

const SchedulePickupDrawer: React.FC<SchedulePickupDrawerProps> = ({
  shipmentId,
  legType,
  toggleDrawer,
}) => {
  const { t } = useTranslation('shipments');
  const { prefill } = usePickupPrefill(shipmentId);
  const { shipmentData } = useFetchSingleShipment(shipmentId, false, 'minimal');

  const justScheduled = useRef(false);

  const backToDetail = () =>
    toggleDrawer('open', 'Detail', shipmentId, undefined, legType);

  // An ambiguous 409/504 can still schedule a pickup; the refetched prefill reveals it.
  const existing =
    prefill?.activePickup?.status === 'SCHEDULED' ? prefill.activePickup : null;
  const accountMissing = !!prefill && !prefill.accountLinked;
  useEffect(() => {
    if (!accountMissing) return;
    message.warning({
      key: 'fedex-pickup-account',
      content: FEDEX_ACCOUNT_MISSING_MESSAGE,
      duration: 8,
    });
    toggleDrawer('open', 'Detail', shipmentId, undefined, legType);
  }, [accountMissing, toggleDrawer, shipmentId, legType]);
  useEffect(() => {
    if (!existing || justScheduled.current) return;
    message.info(
      t(
        'pickup.alreadyScheduled',
        'This shipment already has a scheduled pickup · Confirmation code {{code}}',
        { code: existing.confirmationCode }
      )
    );
    toggleDrawer('open', 'Detail', shipmentId, undefined, legType);
  }, [existing, t, toggleDrawer, shipmentId, legType]);

  // Don't mount the panel for states the effects above redirect away from: it would
  // enable the availability query (blank account) before navigation lands.
  const redirecting = accountMissing || (!!existing && !justScheduled.current);
  if (!prefill || redirecting) return <Spin className='h-full w-full' />;

  return (
    <SchedulePickupPanel
      shipmentId={shipmentId}
      shipmentLabel={shipmentData?.reference_number}
      prefill={prefill}
      onBack={backToDetail}
      onClose={() => toggleDrawer('close', 'all', null)}
      onScheduled={(code) => {
        justScheduled.current = true;
        message.success(
          t(
            'pickup.scheduledToast',
            'Pickup scheduled · Confirmation code {{code}}',
            { code }
          )
        );
        backToDetail();
      }}
    />
  );
};

export default SchedulePickupDrawer;
